import { Link } from 'react-router-dom';

interface VideoCardProps {
  id: number;
  title: string;
  thumbnail: string;
  duration: string;
  views: number;
  date: string;
  category: string;
  categoryName: string;
  isHot?: boolean;
  variant?: 'default' | 'large' | 'compact';
}

function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return views.toString();
}

export default function VideoCard({
  id,
  title,
  thumbnail,
  duration,
  views,
  date,
  category,
  categoryName,
  isHot,
  variant = 'default',
}: VideoCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${id}`}
        className="group flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="relative w-28 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={thumbnail}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          <span className="absolute bottom-1 right-1 bg-black/75 text-background-50 text-[10px] px-1.5 py-0.5 rounded">
            {duration}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
            {title}
          </h4>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
            <span>{categoryName}</span>
            <span>{formatViews(views)}次播放</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'large') {
    return (
      <Link
        to={`/article/${id}`}
        className="group block rounded-xl overflow-hidden bg-background-50 hover:bg-background-100 transition-all duration-300 cursor-pointer"
      >
        <div className="relative aspect-video overflow-hidden">
          <img
            src={thumbnail}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
          <span className="absolute bottom-3 right-3 bg-black/75 text-background-50 text-xs px-2 py-1 rounded-md">
            {duration}
          </span>
          {isHot && (
            <span className="absolute top-3 left-3 bg-primary-500 text-background-50 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
              <i className="ri-fire-line text-xs"></i>热门
            </span>
          )}
        </div>
        <div className="p-4">
          <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
            {title}
          </h3>
          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(views)}次播放
            </span>
            <span>{date}</span>
            <span className="bg-background-200 text-foreground-600 px-2 py-0.5 rounded-full text-[11px]">
              {categoryName}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={thumbnail}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute bottom-2 right-2 bg-black/75 text-background-50 text-[11px] px-1.5 py-0.5 rounded">
          {duration}
        </span>
        {isHot && (
          <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
            <i className="ri-fire-line text-[10px]"></i>热门
          </span>
        )}
      </div>
      <div className="p-3">
        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
          {title}
        </h4>
        <div className="flex items-center gap-2 mt-2 text-[11px] text-foreground-500">
          <span className="flex items-center gap-0.5">
            <i className="ri-eye-line"></i>
            {formatViews(views)}
          </span>
          <span>{date}</span>
        </div>
      </div>
    </Link>
  );
}